import { DeleteIcon, MoreVertIcon } from '@/components/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { WorkItem } from '@/types'
import { getWorkItemStatusTransitions, WorkItemStatuses } from '@/types'
import { Flag, UserPlus } from 'lucide-react'

const itemClassName =
  'h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-normal leading-[1.6] tracking-[0.015em] [&_svg]:size-5'

const menuClassName = 'border-none shadow-elevated rounded-lg py-2 px-0 bg-background gap-0.5 min-w-[180px]'

const testIdSlug = (status: string) => status.toLowerCase().replace(/\s+/g, '-')

interface WorkItemActionsMenuProps {
  workItem: WorkItem
  workItemLabel: string
  canChangeStatus: boolean
  canComplete: boolean
  canAssign: boolean
  canDelete: boolean
  isPending: boolean
  onChangeStatus: (status: string) => void
  onAssign: () => void
  onDelete: () => void
}

export function WorkItemActionsMenu({
  workItem,
  workItemLabel,
  canChangeStatus,
  canComplete,
  canAssign,
  canDelete,
  isPending,
  onChangeStatus,
  onAssign,
  onDelete,
}: WorkItemActionsMenuProps) {
  const statuses = canChangeStatus
    ? getWorkItemStatusTransitions(workItem.status)
        .filter((status) => status !== WorkItemStatuses.Completed || canComplete)
    : []
  const hasStatusSection = statuses.length > 0
  const statusSectionLabelId = `work-item-status-section-${workItem.id}`

  if (!hasStatusSection && !canAssign && !canDelete) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="shrink-0 text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring p-1.5 rounded-lg hover:bg-muted data-[state=open]:bg-muted aria-busy:opacity-50"
          aria-label={`More options for ${workItemLabel}`}
          aria-busy={isPending}
          data-testid={`work-item-menu-trigger-${workItem.id}`}
        >
          <MoreVertIcon className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={4}
        className={menuClassName}
        data-testid={`work-item-menu-${workItem.id}`}
      >
        {hasStatusSection && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className={itemClassName} data-testid={`work-item-change-status-${workItem.id}`}>
              <Flag className="h-5 w-5" />
              Change Status
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className={menuClassName} data-testid={`work-item-status-submenu-${workItem.id}`}>
                <DropdownMenuLabel
                  id={statusSectionLabelId}
                  className="px-4 pt-1 pb-0 text-xs font-normal text-muted-foreground"
                >
                  Change Status
                </DropdownMenuLabel>
                <DropdownMenuGroup aria-labelledby={statusSectionLabelId}>
                  {statuses.map((status) => (
                    <DropdownMenuItem
                      key={status}
                      onClick={() => onChangeStatus(status)}
                      className={itemClassName}
                      data-testid={`work-item-status-action-${testIdSlug(status)}`}
                    >
                      {status}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
        {canAssign && (
          <DropdownMenuItem
            onClick={onAssign}
            className={itemClassName}
            data-testid={`work-item-assign-action-${workItem.id}`}
          >
            <UserPlus className="h-5 w-5" />
            Assign
          </DropdownMenuItem>
        )}
        {(hasStatusSection || canAssign) && canDelete && <DropdownMenuSeparator />}
        {canDelete && (
          <DropdownMenuItem
            onClick={onDelete}
            className={`${itemClassName} text-destructive focus:text-destructive`}
            data-testid={`work-item-delete-action-${workItem.id}`}
          >
            <DeleteIcon className="h-5 w-5" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
